require('./maskedIban.scss');
const PropTypes = require('prop-types');
//@vendor
const React = require('react');
//@commons
const { InputSafe } = require('commonsComponents/inputSafe');
//@helper
const {
    extraValidationCCES,
    extraValidationIbanES,
    isValidIban,
    isValidAccountNumber,
    ibanFromAccountNumber
} = require('utilities/ibanHelper');
const BrowserUtils = require('utilities/browserUtils');

//@constants
const { IBAN_PLACEHOLDER } = require('constants/index');
const { COUNTRY_CODE_ES } = require('commonsConstants/countryCodes');
const SEPA_COUNTRIES = require('constants/countriesSepa');
import { COUNTRIES_SEPA_LENGTH } from 'constants/countriesSepaLength';

class MaskedIban extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            iban: this.setIbanFormat(this.props.iban),
            valid: false
        };
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.countryCode !== nextProps.countryCode) {
            this.setState({
                iban: ''
            });
        } else if (this.state.iban !== nextProps.iban) {
            this.setState({
                iban: this.setIbanFormat(nextProps.iban).toUpperCase()
            });
        }
    }

    handlePaste = () => {
        /*
            Using this validation for IE11 or older versions and clipboardData value since mouse right click paste does
            not trigger onChange properly until react version 15.6. No need to apply this to Edge since its working
            properly.
        */
        if (BrowserUtils.browserIsIE11orBelow()) {
            const customEvt = {
                target: {
                    value: window.clipboardData.getData('Text')
                }
            };

            this.handleChange(customEvt);
        }
    }

    cleanIban = (value) => {
        if (value){
            return value.replace(/[^\d\w]+/g, '').replace(/_+/, '')
        }
        return '';
    }

    setIbanFormat = (value) => {
        if (value){
            return this.cleanIban(value).match(/.{1,4}/g).join(' ').toUpperCase();
        }
        return '';
    }

    checkIfSpaceRemoved = (iban, cursor) => {
        return this.state.iban.length - iban.length == 1 && (cursor + 1) % 5 === 0;
    }

    removeNextChar = (value, cursor) => {
        return value.slice(0, cursor - 1) + value.slice(cursor);
    }

    countSpaces = (value, cursor) => {
        return value.slice(0, cursor).match(/([\s]+)/g) ? value.slice(0, cursor).match(/([\s]+)/g).length : 0;
    }

    handleChange = (e) => {
        const {
            callback,
            countryCode
        } = this.props;
        this.setState({valid: false})
        const element = e.target;
        let value = element.value;
        let cursor = element.selectionStart;
        if (this.checkIfSpaceRemoved(value, cursor)) {
            value = this.removeNextChar(value, cursor);
            cursor--;
        }
        const spaces = this.countSpaces(value, cursor);
        value = this.cleanIban(value);
        const {
            extraCursor,
            valid,
            validated,
            iban,
        } = this.validate(countryCode, value);
        if (validated && callback) {
            this.setState({valid});
            callback(iban.toUpperCase(), valid);
        }
        this.setState({iban: this.setIbanFormat(iban)}, () => {
            element.selectionStart = cursor + extraCursor + Math.floor(cursor/5) - spaces;
            element.selectionEnd = cursor + extraCursor + Math.floor(cursor/5) - spaces;
        })
    }

    handleBlur = () => {
        if (!this.state.valid) {
            const {
                callback,
                countryCode
            } = this.props;
            const {
                valid,
                iban,
            } = this.validate(countryCode, this.cleanIban(this.state.iban), true);
            callback && callback(iban.toUpperCase(), valid);
        }
    }

    validateAccountNumber = (countryCode, value) => {
        let valid = isValidAccountNumber(countryCode, value);
        if (valid && countryCode.toUpperCase() === COUNTRY_CODE_ES) {
            valid = valid && extraValidationCCES(value);
        }

        return {
            extraCursor: valid ? 5 : 0,
            valid,
            validated: true,
            iban: valid ? ibanFromAccountNumber(countryCode, value) : value
        }
    }

    validateIban = (countryCode, value) => {
        let valid;
        const countryCodeIbanTransfer = value.slice(0, 2).toUpperCase();
        const countryCodeTransmitter = countryCode.toUpperCase();
        const isSepaTransfer = SEPA_COUNTRIES.includes(countryCodeIbanTransfer);

        if (isSepaTransfer) {
            valid = isValidIban(value);
        } else {
            valid = countryCodeTransmitter === countryCodeIbanTransfer && isValidIban(value);
        }

        if (valid && countryCodeIbanTransfer === COUNTRY_CODE_ES) {
            valid = valid && extraValidationIbanES(value);
        }

        return {
            extraCursor: 0,
            valid,
            validated: true,
            iban: value
        }
    }

    validate = (countryCode, value, forceValidation=false) => {
        if (value.match(/^[A-Za-z]/) && (forceValidation)) {
            return this.validateIban(countryCode, value);
        }

        if (!value.match(/^[A-Za-z]/) && (COUNTRIES_SEPA_LENGTH[countryCode] <= value.length + 4 || forceValidation)) {
            return this.validateAccountNumber(countryCode, value);
        }

        return {
            extraCursor: 0,
            valid: undefined,
            validated: false,
            iban: value
        }
    }

    render() {
        const {
            id,
            errorState,
            errorMessage,
            label,
            onClickInfo,
            optionalText,
            optionalElement,
            disabled,
            instructionsMessage,
            placeholder,
            doTaggingClickInput,
        } = this.props;

        return (
            <div className="masked-iban">
                <div className="masked-iban__label">
                    <span>{label}</span>
                    <span className="masked-iban__icon icon-informacion" onClick={onClickInfo} />
                </div>
                <div className="masked-iban__optional-element">
                    {optionalElement}
                </div>
                <div className="masked-iban__iban-numbers-container">
                    <InputSafe
                        id={id}
                        type="text"
                        placeholder={placeholder}
                        autoComplete="off"
                        value={this.state.iban}
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        onPaste={() => setTimeout(this.handlePaste)} // setTimeout for the handler to be run after the input text has been updated
                        errorState={errorState}
                        errorMessage={errorMessage}
                        optionalText={optionalText}
                        disabled={disabled}
                        onClick={doTaggingClickInput}
                    />
                </div>
                {
                    instructionsMessage && (
                        <div className="masked-iban__message">
                            {instructionsMessage}
                        </div>
                    )
                }
            </div>
        );
    }
}

MaskedIban.propTypes = {
    id: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    iban: PropTypes.string,
    callback: PropTypes.func,
    errorState: PropTypes.bool,
    errorMessage: PropTypes.string,
    label: PropTypes.string,
    onClickInfo: PropTypes.func,
    optionalText: PropTypes.string,
    optionalElement: PropTypes.object,
    disabled: PropTypes.bool,
    countryCode: PropTypes.string,
    instructionsMessage: PropTypes.string,
    doTaggingClickInput: PropTypes.func,
};

MaskedIban.defaultProps = {
    label: '',
    disabled: false,
    placeholder: IBAN_PLACEHOLDER,
    doTaggingClickInput: () => {},
};

module.exports = MaskedIban;